// @flow
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { FormattedMessage, injectIntl, type IntlShape } from 'react-intl';

import { bindActionCreatorsSafe } from 'util/safeDispatch';
import {
  restartServer,
  startServer,
  stopServer,
} from 'state/commands/adminPropertiesCommands';
import { getIsRestarting, getServerRunState } from 'state/selectors';
import localizedStrings from 'util/localizedStrings';
import {
  SERVER_STATE_STARTING,
  SERVER_STATE_STARTED,
  SERVER_STATE_STOPPED,
  SERVER_STATE_STOPPING,
} from 'util/constants';
import type { Dispatch } from 'state/types';

type Props = {
  intl: IntlShape,
  isRestarting: boolean,
  restartServer: typeof restartServer,
  serverRunState: string,
  startServer: typeof startServer,
  stopServer: typeof stopServer,
};

class ServerStateControls extends Component<Props> {
  handleRestartServer: Function;
  handleStartServer: Function;
  handleStopServer: Function;

  constructor() {
    super();
    this.handleRestartServer = this.handleRestartServer.bind(this);
    this.handleStartServer = this.handleStartServer.bind(this);
    this.handleStopServer = this.handleStopServer.bind(this);
  }

  handleStartServer() {
    this.props.startServer();
  }

  handleStopServer() {
    this.props.stopServer();
  }

  handleRestartServer() {
    this.props.restartServer();
  }

  render() {
    const { intl, isRestarting, serverRunState } = this.props;

    const isTransitionalRunState =
      serverRunState === SERVER_STATE_STARTING ||
      serverRunState === SERVER_STATE_STOPPING ||
      isRestarting;

    let runStateIcon = '';
    let runStateClass = '';
    let runStateLabel = localizedStrings.SERVER_STATE_UNKNOWN;
    let startButtonLabel = localizedStrings.STATUS_START_SERVER;
    let startButtonIcon = 'fa fa-play';
    const stopButtonHidden =
      isTransitionalRunState || serverRunState === SERVER_STATE_STOPPED;
    const startButtonHidden = isTransitionalRunState;

    switch (serverRunState.toLowerCase()) {
      case SERVER_STATE_STARTED:
        runStateIcon = (
          <i className="fa fa-check-circle fa-3x has-text-primary" />
        );
        runStateLabel = localizedStrings.SERVER_STATE_STARTED;
        runStateClass = 'has-text-primary';
        startButtonLabel = localizedStrings.STATUS_RESTART_SERVER;
        startButtonIcon = 'fa fa-repeat fa-rotate-270';
        break;
      case SERVER_STATE_STARTING:
        runStateIcon = (
          <i className="fa fa-circle-o-notch fa-spin fa-2x has-text-primary" />
        );
        runStateLabel = localizedStrings.SERVER_STATE_STARTING;
        runStateClass = 'has-text-primary is-italic';
        break;
      case SERVER_STATE_STOPPED:
        if (isRestarting) {
          runStateIcon = (
            <i className="fa fa-circle-o-notch fa-spin fa-2x has-text-primary" />
          );
          runStateLabel = localizedStrings.SERVER_STATE_STARTING;
          runStateClass = 'has-text-primary is-italic';
        } else {
          runStateIcon = (
            <i className="fa fa-stop-circle fa-3x has-text-danger" />
          );
          runStateLabel = localizedStrings.SERVER_STATE_STOPPED;
          runStateClass = 'has-text-danger';
        }
        break;
      case SERVER_STATE_STOPPING:
        runStateIcon = (
          <i className="fa fa-circle-o-notch fa-spin fa-2x has-text-danger" />
        );
        runStateLabel = localizedStrings.SERVER_STATE_STOPPING;
        runStateClass = 'has-text-danger is-italic';
        break;
      default:
    }

    return (
      <div className="box status-display">
        {runStateIcon}

        <div style={{ width: '24px' }} />

        {/* Server Run State */}
        <div
          className={runStateClass}
          data-test-name={'server-state-' + intl.formatMessage(runStateLabel)}
        >
          Portfolio <FormattedMessage {...runStateLabel} />
        </div>

        <div style={{ width: '96px' }} />

        {/* stop server button */}
        {!stopButtonHidden && (
          <button
            className="button is-primary is-outlined is-medium"
            onClick={this.handleStopServer}
            data-test-name="stop-server"
          >
            <span className="icon">
              <i className="fa fa-stop" />
            </span>
            <FormattedMessage {...localizedStrings.STATUS_STOP_SERVER} />
          </button>
        )}

        <div style={{ width: '32px' }} />

        {/* start/restart server button */}
        {!startButtonHidden && (
          <button
            className={`button is-primary is-medium
                ${
                  serverRunState === SERVER_STATE_STOPPED ? '' : 'is-outlined'
                }`}
            onClick={
              serverRunState === SERVER_STATE_STARTED
                ? this.handleRestartServer
                : this.handleStartServer
            }
            data-test-name="start-restart-server"
          >
            <span className="icon">
              <i className={startButtonIcon} />
            </span>
            <FormattedMessage {...startButtonLabel} />
          </button>
        )}
      </div>
    );
  }
}

let wrappedForm = injectIntl(ServerStateControls);

export default (wrappedForm = connect(
  state => ({
    isRestarting: getIsRestarting(state),
    serverRunState: getServerRunState(state),
  }),
  (dispatch: Dispatch) =>
    bindActionCreatorsSafe({
      restartServer,
      startServer,
      stopServer,
    })
)(wrappedForm));



// WEBPACK FOOTER //
// ./src/view/Pages/StatusPage/ServerStateControls.js